import {
  resolveOrderDashboardCategory,
  resolveOrderDetailPath,
} from "./orders/resolveOrderProductDisplay";

/**
 * Agent dashboard orders: `/p/dashboard/orders/:category`
 * Index order matches Orders.jsx tabs (0..4).
 */
export const ORDERS_DASHBOARD_BASE = "/p/dashboard/orders";

export const ORDERS_CATEGORY_SLUGS = [
  "packages",
  "flights",
  "hotels",
  "dynamic-packages",
  "organized-tours",
];

export const DEFAULT_ORDERS_CATEGORY_SLUG = ORDERS_CATEGORY_SLUGS[0];

/** @param {string | undefined} slug */
export function ordersTabIndexFromSlug(slug) {
  if (!slug || typeof slug !== "string") return null;
  const i = ORDERS_CATEGORY_SLUGS.indexOf(slug);
  return i >= 0 ? i : null;
}

/** @param {number} index */
export function ordersSlugFromTabIndex(index) {
  return ORDERS_CATEGORY_SLUGS[index] ?? null;
}

/** @param {string} slug */
export function ordersDashboardCategoryPath(slug) {
  return `${ORDERS_DASHBOARD_BASE}/${slug}`;
}

/** פירוט הזמנה — חבילות נופש (LDS vacation). */
export function ordersPackageDetailPath(orderId) {
  return `${ORDERS_DASHBOARD_BASE}/packages/${encodeURIComponent(String(orderId))}`;
}

/** פירוט הזמנה — טיסות LDS (אותה קומפוננטת פירוט). */
export function ordersFlightDetailPath(orderId) {
  return `${ORDERS_DASHBOARD_BASE}/flights/${encodeURIComponent(String(orderId))}`;
}

/** פירוט הזמנה — מלון (Gimmonix). */
export function ordersHotelDetailPath(orderId) {
  return `${ORDERS_DASHBOARD_BASE}/hotels/${encodeURIComponent(String(orderId))}`;
}

/** פירוט הזמנה — חבילה דינמית (לא מודאל). */
export function ordersDynamicPackageDetailPath(orderId) {
  return `${ORDERS_DASHBOARD_BASE}/dynamic-packages/${encodeURIComponent(String(orderId))}`;
}

/** פירוט הזמנה — טיול מאורגן (LDS, אותה קומפוננטת פרימיום כמו חבילות). */
export function ordersOrganizedTourDetailPath(orderId) {
  return `${ORDERS_DASHBOARD_BASE}/organized-tours/${encodeURIComponent(String(orderId))}`;
}

/**
 * Pick category slug from `orderType` query used by deep-links into Orders.
 * @param {string | null} orderType
 */
export function ordersCategorySlugFromOrderTypeParam(orderType) {
  if (orderType === "package" || orderType === "lds") return "packages";
  if (orderType === "flight" || orderType === "flights") return "flights";
  if (orderType === "organized_tour") return "organized-tours";
  if (orderType === "dynamic") return "dynamic-packages";
  return "hotels";
}

/**
 * Lobby deep-links only: returns a known slug or `null` if `orderType` is missing/unknown.
 * (Unlike `ordersCategorySlugFromOrderTypeParam`, does not default to hotels.)
 * @param {string | null | undefined} orderType
 */
export function ordersCategorySlugFromExtendedOrderTypeParam(orderType) {
  if (orderType == null) return null;
  const raw = String(orderType).trim();
  if (!raw) return null;
  const t = raw.toLowerCase();
  if (t === "package" || t === "packages" || t === "lds" || t === "flightshotel") {
    return "packages";
  }
  if (t === "flight" || t === "flights") return "flights";
  if (t === "hotel" || t === "hotels") return "hotels";
  if (t === "dynamic") return "dynamic-packages";
  if (
    t === "organized" ||
    t === "organized_tour" ||
    t === "organized-tours" ||
    t === "organized_tours"
  ) {
    return "organized-tours";
  }
  return null;
}

/**
 * `/p/dashboard/orders` lobby: derive target `orders/:category` from query only when unambiguous.
 * @param {URLSearchParams | Record<string, string>} searchParams
 * @returns {string|null}
 */
export function resolveOrdersLobbyCategorySlugFromSearchParams(searchParams) {
  const sp =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams);
  const rawCat = sp.get("category");
  if (rawCat) {
    const trimmed = rawCat.trim();
    const normalized = trimmed.toLowerCase();
    const found = ORDERS_CATEGORY_SLUGS.find(
      (s) => s === trimmed || s === normalized
    );
    if (found) return found;
  }
  return ordersCategorySlugFromExtendedOrderTypeParam(sp.get("orderType"));
}

/**
 * Build full orders URL with query string, using the correct category path segment.
 * @param {URLSearchParams | Record<string, string>} params
 */
export function ordersDashboardUrlFromSearchParams(params) {
  const sp = params instanceof URLSearchParams ? params : new URLSearchParams(params);
  const slug = ordersCategorySlugFromOrderTypeParam(sp.get("orderType"));
  const qs = sp.toString();
  return qs ? `${ORDERS_DASHBOARD_BASE}/${slug}?${qs}` : `${ORDERS_DASHBOARD_BASE}/${slug}`;
}

const ORDER_TYPE_QUERY_BY_CATEGORY = {
  packages: "package",
  flights: "flights",
  hotels: "hotel",
  "dynamic-packages": "dynamic",
  "organized-tours": "organized_tour",
};

/**
 * Recent Orders row click — order detail page (matches Orders list card navigation).
 * Avoids list-tab `?orderId=` filters that miss paginated rows and race filter hydration.
 * @param {Record<string, unknown> | null | undefined} order
 */
export function resolveRecentOrderDashboardDetailPath(order) {
  const fromApi = String(order?.detail_path ?? order?.detailPath ?? "").trim();
  if (fromApi && fromApi.includes("/p/dashboard/orders/")) {
    return fromApi;
  }
  return resolveOrderDetailPath(order);
}

/**
 * Deep-link into orders list tab with query filters (calendar / commission widgets).
 * Prefer `resolveRecentOrderDashboardDetailPath` for Recent Orders home feed rows.
 * @param {Record<string, unknown> | null | undefined} order
 */
export function buildRecentOrderDashboardNavigationUrl(order) {
  const orderId = String(order?.id ?? order?.orderId ?? order?.order_id ?? "").trim();
  const category = resolveOrderDashboardCategory(order);
  const params = new URLSearchParams();
  if (orderId) {
    params.set("orderId", orderId);
  }
  const orderType = ORDER_TYPE_QUERY_BY_CATEGORY[category];
  if (orderType) {
    params.set("orderType", orderType);
  }
  const qs = params.toString();
  return qs
    ? `${ordersDashboardCategoryPath(category)}?${qs}`
    : ordersDashboardCategoryPath(category);
}

/**
 * Loose UUID detector (RFC-style 8-4-4-4-12 hex) — lobby quick-search UX only.
 * @param {string} raw
 */
export function ordersQuickSearchLooksLikeUuid(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    s
  );
}

/**
 * Lobby → חבילות נופש עם `orderId` + `orderType=package` כדי ש־Orders.jsx ישב על טאב חבילות
 * ויישום initialFilters על `order_id` (לא מבצע fetch בלובי).
 * @param {string | undefined | null} rawQuery
 * @returns {string|null}
 */
export function buildOrdersSearchPath(rawQuery) {
  const trimmed = String(rawQuery ?? "").trim();
  if (!trimmed) return null;
  const params = new URLSearchParams();
  params.set("orderId", trimmed);
  params.set("orderType", "package");
  return `${ordersDashboardCategoryPath("packages")}?${params.toString()}`;
}
